import {Component, EventEmitter, Input, OnInit, Optional, Output, ViewChild} from '@angular/core';
import {NzModalRef} from 'ng-zorro-antd/modal';
import {NzUploadChangeParam, NzUploadFile, NzUploadXHRArgs} from 'ng-zorro-antd/upload';
import {
  catchError, concatMap,
  filter,
  finalize,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subscription
} from 'rxjs';
import {HttpClient, HttpEventType, HttpResponse} from '@angular/common/http';
import {NzNotificationRef, NzNotificationService} from 'ng-zorro-antd/notification';
import {
  AiBatchServiceOrderUploadResult,
  Document,
  DocumentLinkCreate,
  DocumentValidationCreate, EntityType,
  Partner,
  PostResponse,
  ServiceOrder,
  ToastConfig,
  User
} from '@app/shared';
import {NzDrawerRef} from 'ng-zorro-antd/drawer';
import {DocumentRestrictionsService, DocumentService, ScreenService, compareById} from '@app/core';
import {tap} from 'rxjs/operators';
import {ProjectUtilsService} from '../../../features/project/services/project-utils.service';
import {AuthService} from '../../../core/services/auth.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {GlobalValuesService} from '../../../core/services/global-values.service';


interface GeneralModalData {
  type: 'upload' | 'correct' | 'validate' | 'reject' | 'return' | 'delete';
  title: string;
  actionIcon: string;
  actionTitle: string;
}

export interface UploadFormData {
  documentType?: string;
  documentTypeId?: number;
  file: File;
  expirationDate: Date;
  progress?: number; // Porcentaje de subida (0 a 100)
  status: 'pending' | 'uploading' | 'success' | 'error';
  ownerId: number;
  serviceOrderId?: number; // Para modo múltiple SO
}

export interface ServiceOrderUploadData {
  serviceOrder: ServiceOrder;
  documentType?: string;
  documentTypeId?: number;
  expirationDate: Date;
  progress?: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  errorMessage?: string;
}

interface UploadExecutionResult {
  success: boolean;
  data?: Document;
  errorMessage?: string;
  form?: UploadFormData;
  serviceOrderUploadData?: ServiceOrderUploadData;
}


@Component({
  selector: 'app-doc-modal',
  standalone: false,

  templateUrl: './doc-modal.component.html',
  styleUrl: './doc-modal.component.less'
})
export class DocModalComponent implements OnInit {

  generalDatas: GeneralModalData[] = [
    {type: 'upload', title: 'Subir documentos', actionIcon: 'upload', actionTitle: 'Subir'},
    {type: 'correct', title: 'Corregir documento', actionIcon: 'upload', actionTitle: 'Corregir documento'},
    {type: 'validate', title: 'Validar documento', actionIcon: 'check_circle', actionTitle: 'Validar'},
    {
      type: 'return',
      title: 'Volver a pendiente de validación',
      actionIcon: '',
      actionTitle: 'Volver a pendiente de validación'
    },
    {type: 'reject', title: 'Rechazar documento', actionIcon: 'close', actionTitle: 'Rechazar'},
    {type: 'delete', title: 'Eliminar documento', actionIcon: 'check_circle', actionTitle: 'Eliminar'},
  ];
  currentGeneralData: GeneralModalData | undefined;
  // Variables de 'upload'
  @ViewChild('uploadComponent') uploadComponent: any;
  uploadForms: UploadFormData[] = [];

  // Variables del resto de tipos
  private _aiUploadMode: boolean = false;
  @Input()
  set aiUploadMode(value: boolean) {
    this._aiUploadMode = value;
    this.updateCurrentGeneralData();
    this.refreshDocumentTypes();
  }
  get aiUploadMode(): boolean {
    return this._aiUploadMode;
  }
  @Input() onlyReturnFiles: boolean = false;
  @Input() document: Document | undefined;
  @Input() documentsToDelete: Document[] | undefined;
  @Input() entityId: number = 0;
  @Input() entityType: EntityType = 'Project';
  @Input() documentType: 'MBL' | 'HBL' | undefined;
  private _serviceOrders: ServiceOrder[] | undefined;
  @Input()
  set serviceOrders(value: ServiceOrder[] | undefined) {
    this._serviceOrders = value;
    this.initializeModeFromInputs();
  }
  get serviceOrders(): ServiceOrder[] | undefined {
    return this._serviceOrders;
  }
  rejectReason: string | undefined;

  // Variables para modo múltiple SO
  isMultipleSOMode: boolean = false;
  globalDocumentType: string | undefined;
  globalExpirationDate: Date = new Date();
  serviceOrderUploadData: ServiceOrderUploadData[] = [];
  singleFile: any; // Solo un archivo para múltiples SOs

  @Output() deleteEvent: EventEmitter<Document[]> = new EventEmitter<Document[]>();
  @Output() createEvent: EventEmitter<Document[]> = new EventEmitter<Document[]>();
  @Output() proccessFilesEvent: EventEmitter<File[]> = new EventEmitter<File[]>();

  // Notifications variables
  @ViewChild('notificationTmpl') notificationTmpl: any;
  toastConfig: ToastConfig = {
    icon: 'check_circle',
    iconColor: '#6CDA75',
    content: 'Los archivos se han subido.',
    showUndo: false
  };

  notRef: NzNotificationRef | undefined;

  isMobile$;
  partnerOptions: Partner[] = [];
  documentTypes: {name: string, id: number}[] = [];
  private allDocumentTypes: {name: string, id: number}[] = [];

  compareById = compareById;

  loading: boolean = false;

  loggedUser: User | undefined;

  newDocuments: Document[] = [];
  private ignoredInvalidFileKeys = new Set<string>();
  private ignoredFilesToastTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(@Optional() private modalRef: NzModalRef<DocModalComponent>,
              @Optional() public drawerRef: NzDrawerRef<DocModalComponent>,
              private http: HttpClient,
              private screenService: ScreenService,
              private notificationService: NzNotificationService,
              private documentService: DocumentService,
              private documentRestrictionsService: DocumentRestrictionsService,
              private ps: ProjectUtilsService,
              public authService: AuthService,
              public globalValuesService:GlobalValuesService
  ) {
    this.isMobile$ = screenService.isMobile$;
    // this.partnerOptions = ps.partnersOptions;
    // this.documentTypes = ps.documentTypes;
    authService.loggedUser
      .pipe(takeUntilDestroyed())
      .subscribe((value) => this.loggedUser = value);
    this.globalValuesService.documentTypes$.pipe(takeUntilDestroyed())
      .subscribe((value) => {
        if (value) {
          this.allDocumentTypes = value;
          this.refreshDocumentTypes();
        }
      });
    this.globalValuesService.isPartnerReady$.pipe(takeUntilDestroyed())
      .subscribe(value => {
        if(value){
          this.partnerOptions=this.globalValuesService.partnerlist;
        }
      })
  }

  ngOnInit(): void {
    this.initializeModeFromInputs();
  }

  private refreshDocumentTypes(): void {
    // Con IA solo se ofrece el BL: es el único documento que el motor sabe leer.
    if (this.aiUploadMode) {
      this.documentTypes = this.documentRestrictionsService.getAiUploadDocumentTypes(this.allDocumentTypes);
      this.applyAiDocumentType();
      return;
    }

    this.documentTypes = !!this.authService.loggedUser$.value?.uploadDocumentsLimited
      ? this.documentRestrictionsService.getLimitedUploadDocumentTypes(this.allDocumentTypes) : this.allDocumentTypes;
  }

  /** Tipo fijado en la subida con IA cuando solo hay uno posible. */
  get aiDocumentType(): {name: string, id: number} | undefined {
    return this.aiUploadMode && this.documentTypes.length === 1 ? this.documentTypes[0] : undefined;
  }

  get aiDocumentTypeMissing(): boolean {
    return this.aiUploadMode && this.allDocumentTypes.length > 0 && this.documentTypes.length === 0;
  }

  private applyAiDocumentType(): void {
    const aiDocumentType = this.aiDocumentType;
    if (!aiDocumentType) {
      return;
    }

    this.uploadForms.forEach(form => form.documentType = aiDocumentType.name);
    if (this.isMultipleSOMode) {
      this.globalDocumentType = aiDocumentType.name;
      this.serviceOrderUploadData.forEach(data => {
        data.documentType = aiDocumentType.name;
        data.documentTypeId = aiDocumentType.id;
      });
    }
  }

  initializeModeFromInputs(): void {
    // Detectar si estamos en modo múltiple SO
    if (this.serviceOrders && this.serviceOrders.length > 0 && this.docModalType === 'upload') {
      this.isMultipleSOMode = true;
      this.entityType = 'ServiceOrder';
      this.initializeMultipleSOMode();
    } else {
      this.isMultipleSOMode = false;
    }
  }

  initializeMultipleSOMode(): void {
    const defaultExpiration = new Date();
    defaultExpiration.setMonth(defaultExpiration.getMonth() + 1);
    this.globalExpirationDate = defaultExpiration;

    // Inicializar datos de upload para cada SO
    this.serviceOrderUploadData = this.serviceOrders!.map(so => ({
      serviceOrder: so,
      documentType: undefined,
      documentTypeId: undefined,
      expirationDate: new Date(defaultExpiration),
      status: 'pending' as const,
      progress: 0
    }));
    this.applyAiDocumentType();
  }
  private _docModalType: any;

  get docModalType(): 'upload' | 'correct' | 'validate' | 'reject' | 'return' | 'delete' {
    return this._docModalType;
  }

  @Input()
  set docModalType(value: 'upload' | 'correct' | 'validate' | 'reject' | 'return' | 'delete') {
    this._docModalType = value;
    this.updateCurrentGeneralData();
    this.initializeModeFromInputs();
  }

  get allFormsValid(): boolean {
    if (this.aiDocumentTypeMissing) {
      return false;
    }
    if (this.isMultipleSOMode) {
      // En modo múltiple SO, necesitamos el archivo y que todas las SO tengan documentType
      return !!this.singleFile &&
             this.serviceOrderUploadData.length > 0 &&
             this.serviceOrderUploadData
               .every(data => data.documentType && data.documentType.trim() !== '' && data.status === 'pending');
    }
    return this.uploadForms.length > 0 && this.uploadForms
      .every(form => form.documentType && form.documentType.trim() !== '' && form.status === 'pending');
  }

  get uploadAccept(): string | null {
    return this.docModalType === 'upload' ? '.pdf,application/pdf' : null;
  }

  close(): void {
    this.modalRef?.close('closed');
    this.drawerRef?.close('closed');
  }

  // Este método se dispara cuando se selecciona uno o varios archivos
  handleUploadChange(info: NzUploadChangeParam): void {
    const defaultExpiration = new Date();
    defaultExpiration.setMonth(defaultExpiration.getMonth() + 1);
    const sanitizedFiles = info.fileList.filter((file): file is NzUploadFile & { originFileObj: File } => !!file.originFileObj);
    const {validFiles, invalidFiles} = this.filterFilesForCurrentAction(sanitizedFiles);

    this.syncUploadComponentFileList(validFiles);

    if (invalidFiles.length > 0) {
      this.queueIgnoredFilesNotification(invalidFiles);
    }

    // Modo multiple SO: solo acepta un archivo
    if (this.isMultipleSOMode) {
      const lastFile: NzUploadFile | undefined = validFiles[validFiles.length - 1];
      if (lastFile) {
        this.singleFile = lastFile.originFileObj as File;
        this.syncUploadComponentFileList([lastFile]);
      } else if (validFiles.length === 0) {
        this.singleFile = undefined;
      }
      return;
    }

    if (this.document) {
      const lastFile: NzUploadFile | undefined = validFiles[validFiles.length - 1];
      if (lastFile) {
        this.uploadForms = [{
          documentType: this.onlyReturnFiles ? 'MBL' : (this.document.type || this.documentType || ''),
          status: 'pending',
          expirationDate: defaultExpiration,
          file: lastFile.originFileObj as File,
          ownerId: this.loggedUser!.partnerId,
        }];
      } else {
        this.uploadForms = [];
      }
    } else {
      this.uploadForms = validFiles.reduce((acc: UploadFormData[], file: NzUploadFile) => {
        const newItem: UploadFormData = {
          documentType: this.onlyReturnFiles ? 'MBL' : (this.document?.type || this.documentType || this.aiDocumentType?.name || ''),
          status: 'pending',
          expirationDate: defaultExpiration,
          file: file.originFileObj as File,
          ownerId: this.loggedUser!.partnerId,
        };
        const index = acc.findIndex(item => item.file.name === file.name);
        if (index !== -1) {
          acc[index] = newItem;
        } else {
          acc.push(newItem);
        }
        return acc;
      }, []);
    }
  }
  private filterFilesForCurrentAction(files: NzUploadFile[]): { validFiles: NzUploadFile[], invalidFiles: NzUploadFile[] } {
    if (this.docModalType !== 'upload') {
      return {validFiles: files, invalidFiles: []};
    }

    const validFiles = files.filter(file => this.isPdfFile(file.originFileObj as File));
    const invalidFiles = files.filter(file => !this.isPdfFile(file.originFileObj as File));
    return {
      validFiles,
      invalidFiles
    };
  }

  private isPdfFile(file: File | undefined): boolean {
    if (!file) {
      return false;
    }

    const fileType = file.type?.toLowerCase();
    const fileName = file.name?.toLowerCase() || '';

    return fileType === 'application/pdf' || fileName.endsWith('.pdf');
  }

  private syncUploadComponentFileList(files: NzUploadFile[]): void {
    if (this.uploadComponent) {
      this.uploadComponent.nzFileList = files;
    }
  }

  private queueIgnoredFilesNotification(files: NzUploadFile[]): void {
    files.forEach(file => this.ignoredInvalidFileKeys.add(this.getUploadFileKey(file)));

    if (this.ignoredFilesToastTimeout) {
      return;
    }

    this.ignoredFilesToastTimeout = setTimeout(() => {
      const ignoredCount = this.ignoredInvalidFileKeys.size;
      this.ignoredInvalidFileKeys.clear();
      this.ignoredFilesToastTimeout = null;

      if (ignoredCount > 0) {
        this.showIgnoredFilesNotification(ignoredCount);
      }
    }, 50);
  }

  private getUploadFileKey(file: NzUploadFile): string {
    return file.uid || `${file.name}-${file.size ?? '0'}`;
  }

  private showIgnoredFilesNotification(count: number): void {
    this.toastConfig = {
      icon: 'warning',
      iconColor: '#CB2A2F',
      content: count === 1
        ? '1 documento no se tiene en cuenta por no ser del formato correcto.'
        : `${count} documentos no se tienen en cuenta por no ser del formato correcto.`,
      showUndo: false
    };
    this.showToast();
  }

  deleteDocumentToUpload(form: UploadFormData): void {
    this.uploadForms = this.uploadForms.filter(item => item.file.name !== form.file.name);

    // Actualiza también la lista interna del nz-upload
    if (this.uploadComponent && this.uploadComponent.nzFileList) {
      this.uploadComponent.nzFileList = this.uploadComponent.nzFileList.filter(
        (f: NzUploadFile) => f.name !== form.file.name
      );
    }
  }

  // Métodos para modo múltiple SO
  updateGlobalDocumentType(value: string): void {
    this.globalDocumentType = value;
    // Actualizar todas las filas de SO
    this.serviceOrderUploadData.forEach(data => {
      data.documentType = value;
      const docType = this.documentTypes.find(dt => dt.name === value);
      data.documentTypeId = docType?.id;
    });
  }

  updateGlobalExpirationDate(value: Date): void {
    this.globalExpirationDate = value;
    // Actualizar todas las filas de SO
    this.serviceOrderUploadData.forEach(data => {
      data.expirationDate = new Date(value);
    });
  }

  updateSODocumentType(soData: ServiceOrderUploadData, value: string): void {
    soData.documentType = value;
    const docType = this.documentTypes.find(dt => dt.name === value);
    soData.documentTypeId = docType?.id;
  }

  updateSOExpirationDate(soData: ServiceOrderUploadData, value: Date): void {
    soData.expirationDate = new Date(value);
  }

  deleteSOFromUpload(soData: ServiceOrderUploadData): void {
    this.serviceOrderUploadData = this.serviceOrderUploadData.filter(data => data.serviceOrder.id !== soData.serviceOrder.id);
    // También remover del array de serviceOrders
    if (this.serviceOrders) {
      this._serviceOrders = this.serviceOrders.filter(so => so.id !== soData.serviceOrder.id);
    }
  }

  startMultipleSOUpload(): void {
    if (this.aiUploadMode) {
      this.startMultipleSOAiUpload();
      return;
    }

    if (!this.singleFile) return;
    this.newDocuments = [];

    const uploadObservables: Observable<UploadExecutionResult>[] = this.serviceOrderUploadData.map((soData: ServiceOrderUploadData) => {
      soData.status = 'uploading';
      soData.progress = 0;

      const name = this.singleFile!.name.split('.')[0];
      const documentTypeId = soData.documentTypeId || this.documentTypes.find(dt => dt.name === soData.documentType)!.id;

      const formData = new FormData();
      formData.append('file', this.singleFile, this.singleFile.name);
      formData.append('type', soData.documentType!);
      formData.append('name', name);
      formData.append('documentTypeId', documentTypeId.toString());
      formData.append('expirationDate', soData.expirationDate.toISOString());
      formData.append('ownerId', this.loggedUser!.partnerId.toString());
      formData.append('partnerId', this.loggedUser!.partnerId.toString());

      return this.documentService.createDocument(formData).pipe(
        tap(event => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            soData.progress = Math.round(100 * event.loaded / event.total);
          }
        }),
        filter(event => event.type === HttpEventType.Response),
        map(event => (event as HttpResponse<PostResponse>).body),
        mergeMap((documentResponse: any) => {
          if (!documentResponse?.success) {
            return of({
              success: false,
              errorMessage: documentResponse?.message || 'No se pudo subir el documento.',
              serviceOrderUploadData: soData
            });
          }

          const documentLinkPayload: DocumentLinkCreate = {
            documentId: documentResponse.data.id,
            innerLinkDocumentPayload: {
              isLinked: true,
              entityId: soData.serviceOrder.id!
            },
            entityType: 'ServiceOrder' as EntityType,
          };

          return this.documentService.createDocumentLink(documentLinkPayload).pipe(
            map(linkResponse => ({
              success: !!linkResponse.success,
              data: linkResponse.success ? documentResponse.data : undefined,
              errorMessage: linkResponse.success ? undefined : (linkResponse.message || 'No se pudo vincular el documento.'),
              serviceOrderUploadData: soData
            })),
            catchError(linkError => {
              console.error('Error vinculando documento:', linkError);
              return of({
                success: false,
                errorMessage: this.getRequestErrorMessage(linkError, 'No se pudo vincular el documento.'),
                serviceOrderUploadData: soData
              });
            })
          );
        }),
        catchError((error: any) => of({
          success: false,
          errorMessage: this.getRequestErrorMessage(error, 'No se pudo subir el documento.'),
          serviceOrderUploadData: soData
        }))
      );
    });

    const uploadResults: UploadExecutionResult[] = [];

    from(uploadObservables).pipe(
      concatMap((obs) => obs)
    ).subscribe({
      next: (result: UploadExecutionResult) => {
        uploadResults.push(result);

        if (result.serviceOrderUploadData) {
          result.serviceOrderUploadData.status = result.success ? 'success' : 'error';
          result.serviceOrderUploadData.progress = 100;
        }

        if (result.success && result.data) {
          this.newDocuments.push(result.data);
        }
      },
      error: (error) => {
        console.error(error);
        this.toastConfig = {
          icon: 'dangerous',
          iconColor: '#CB2A2F',
          content: 'Ha ocurrido un error subiendo los documentos.',
          msg: this.getRequestErrorMessage(error),
          showUndo: false
        };
        this.showNotification(false);
      },
      complete: () => {
        this.setUploadSummaryToast(uploadResults);
        this.createEvent.emit(this.newDocuments);
        this.showNotification(uploadResults.every(result => result.success));
      }
    });
  }

  startMultipleSOAiUpload(): void {
    if (!this.singleFile) return;

    const documentData = this.serviceOrderUploadData[0];
    const documentTypeId = documentData.documentTypeId || this.documentTypes.find(dt => dt.name === documentData.documentType)?.id;

    if (!documentTypeId) {
      this.toastConfig = {
        icon: 'dangerous',
        iconColor: '#CB2A2F',
        content: 'No se encontro el tipo de documento seleccionado.',
        msg: 'Seleccione un tipo de documento antes de continuar.',
        showUndo: false
      };
      this.showNotification(false);
      return;
    }

    this.serviceOrderUploadData.forEach(data => {
      data.status = 'uploading';
      data.progress = 0;
      data.errorMessage = undefined;
    });

    const name = this.singleFile.name.split('.')[0];
    const formData = new FormData();
    formData.append('file', this.singleFile, this.singleFile.name);
    formData.append('type', documentData.documentType!);
    formData.append('name', name);
    formData.append('documentTypeId', documentTypeId.toString());
    formData.append('expirationDate', documentData.expirationDate.toISOString());
    formData.append('ownerId', this.loggedUser!.partnerId.toString());
    formData.append('partnerId', this.loggedUser!.partnerId.toString());
    this.serviceOrderUploadData.forEach(data => {
      formData.append('serviceOrderIds', data.serviceOrder.id!.toString());
    });

    this.documentService.uploadForAiAnalysisBatchServiceOrders(formData).pipe(
      tap(event => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          const progress = Math.round(100 * event.loaded / event.total);
          this.serviceOrderUploadData.forEach(data => data.progress = progress);
        }
      }),
      filter(event => event.type === HttpEventType.Response),
      map(event => (event as HttpResponse<PostResponse>).body),
      catchError((error: any) => of({
        success: false,
        message: this.getRequestErrorMessage(error, 'No se pudo subir y analizar el documento.'),
        data: undefined
      } as PostResponse))
    ).subscribe((response: PostResponse | null | undefined) => {
      const normalizedResponse = response || undefined;
      const batchResult = normalizedResponse?.data as AiBatchServiceOrderUploadResult | undefined;
      this.applyAiBatchResultToRows(batchResult, normalizedResponse);
      this.setAiBatchSummaryToast(batchResult, normalizedResponse);

      const shouldClose = !!normalizedResponse?.success && !!batchResult && batchResult.linkedCount > 0;
      if (shouldClose) {
        this.createEvent.emit([]);
      }
      this.showNotification(shouldClose);
    });
  }

  startUpload(): void {
    if (this.isMultipleSOMode) {
      this.startMultipleSOUpload();
      return;
    }
    this.newDocuments = [];

    const uploadObservables: Observable<UploadExecutionResult>[] = this.uploadForms.map((form: UploadFormData) => {
      form.status = 'uploading';
      form.progress = 0;

      const name = form.file.name.split('.')[0];
      const documentTypeId = this.documentTypes.find(dt => dt.name === form.documentType)!.id;

      const formData = new FormData();
      formData.append('file', form.file, form.file.name);
      formData.append('type', form.documentType!);
      formData.append('name', name);
      formData.append('documentTypeId', documentTypeId.toString());
      formData.append('expirationDate', form.expirationDate.toISOString());
      formData.append('ownerId', form.ownerId.toString());
      formData.append('partnerId', this.loggedUser!.partnerId.toString());
      if (this.aiUploadMode && this.entityType === 'ServiceOrder') {
        formData.append('serviceOrderId', this.entityId.toString());
      }

      const uploadObs = this.aiUploadMode
        ? this.documentService.uploadForAiAnalysis(formData)
        : this.documentService.createDocument(formData);

      return uploadObs.pipe(
        tap(event => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            form.progress = Math.round(100 * event.loaded / event.total);
          }
        }),
        filter(event => event.type === HttpEventType.Response),
        map(event => (event as HttpResponse<PostResponse>).body),
        mergeMap((documentResponse: any) => {
          if (!documentResponse?.success) {
            return of({
              success: false,
              errorMessage: documentResponse?.message || 'No se pudo subir el documento.',
              form
            });
          }

          if (this.aiUploadMode && documentResponse?.data) {
            documentResponse.data.aiProcessingStatus = 'Processing';
          }

          const documentLinkPayload: DocumentLinkCreate = {
            documentId: documentResponse.data.id,
            innerLinkDocumentPayload: {
              isLinked: true,
              entityId: this.entityId
            },
            entityType: this.entityType,
          };

          return this.documentService.createDocumentLink(documentLinkPayload).pipe(
            map(linkResponse => ({
              success: !!linkResponse.success,
              data: linkResponse.success ? documentResponse.data : undefined,
              errorMessage: linkResponse.success ? undefined : (linkResponse.message || 'No se pudo vincular el documento.'),
              form
            })),
            catchError(linkError => {
              console.error('Error vinculando documento:', linkError);
              return of({
                success: false,
                errorMessage: this.getRequestErrorMessage(linkError, 'No se pudo vincular el documento.'),
                form
              });
            })
          );
        }),
        catchError((error: any) => of({
          success: false,
          errorMessage: this.getRequestErrorMessage(error, 'No se pudo subir el documento.'),
          form
        }))
      );
    });

    const uploadResults: UploadExecutionResult[] = [];

    from(uploadObservables).pipe(
      concatMap((obs) => obs)
    ).subscribe({
      next: (result: UploadExecutionResult) => {
        uploadResults.push(result);

        if (result.form) {
          result.form.status = result.success ? 'success' : 'error';
          result.form.progress = 100;
        }

        if (result.success && result.data) {
          this.newDocuments.push(result.data);
        }
      },
      error: (error) => {
        console.error(error);
        this.toastConfig = {
          icon: 'dangerous',
          iconColor: '#CB2A2F',
          content: 'Ha ocurrido un error subiendo los documentos.',
          msg: this.getRequestErrorMessage(error),
          showUndo: false
        };
        this.showNotification(false);
      },
      complete: () => {
        this.setUploadSummaryToast(uploadResults);
        this.createEvent.emit(this.newDocuments);
        this.showNotification(uploadResults.every(result => result.success));
      }
    });
  }
  private setUploadSummaryToast(results: UploadExecutionResult[]): void {
    const successCount = results.filter(result => result.success).length;
    const errorResults = results.filter(result => !result.success);
    const errorCount = errorResults.length;
    const firstErrorMessage = errorCount === 1 ? errorResults[0].errorMessage : undefined;

    if (successCount > 0 && errorCount === 0) {
      this.toastConfig = {
        icon: 'check_circle',
        iconColor: '#6CDA75',
        content: successCount === 1
          ? '1 documento se ha subido correctamente.'
          : `${successCount} documentos se han subido correctamente.`,
        showUndo: false
      };
      return;
    }

    if (successCount > 0) {
      this.toastConfig = {
        icon: 'warning',
        iconColor: '#CB2A2F',
        content: `${successCount} ${successCount === 1 ? 'documento se ha subido' : 'documentos se han subido'} y ${errorCount} ${errorCount === 1 ? 'documento no se pudo procesar' : 'documentos no se pudieron procesar'}.`,
        msg: firstErrorMessage,
        showUndo: false
      };
      return;
    }

    this.toastConfig = {
      icon: 'dangerous',
      iconColor: '#CB2A2F',
      content: errorCount === 1
        ? 'No se pudo subir el documento.'
        : 'No se pudo subir ningun documento.',
      msg: firstErrorMessage,
      showUndo: false
    };
  }

  private applyAiBatchResultToRows(
    batchResult: AiBatchServiceOrderUploadResult | undefined,
    response: PostResponse | undefined
  ): void {
    const links = batchResult?.serviceOrderLinks || [];

    this.serviceOrderUploadData.forEach(data => {
      const link = links.find(item => item.serviceOrderId === data.serviceOrder.id);
      const linked = !!link?.linked;
      data.status = linked ? 'success' : 'error';
      data.progress = 100;
      data.errorMessage = linked ? undefined : (link?.error || response?.message || 'No se pudo vincular la OS.');
    });
  }

  private setAiBatchSummaryToast(
    batchResult: AiBatchServiceOrderUploadResult | undefined,
    response: PostResponse | undefined
  ): void {
    if (!response?.success || !batchResult) {
      this.toastConfig = {
        icon: 'dangerous',
        iconColor: '#CB2A2F',
        content: 'No se pudo subir y analizar el documento.',
        msg: response?.message,
        showUndo: false
      };
      return;
    }

    const total = this.serviceOrderUploadData.length;
    const linkedCount = batchResult.linkedCount || 0;
    const failedLinks = batchResult.serviceOrderLinks?.filter(link => !link.linked) || [];
    const firstError = failedLinks[0]?.error;

    if (linkedCount === 0) {
      this.toastConfig = {
        icon: 'dangerous',
        iconColor: '#CB2A2F',
        content: 'Documento subido, pero no se pudo vincular a las OS.',
        msg: firstError || response.message,
        showUndo: false
      };
      return;
    }

    if (batchResult.aiProcessingStatus === 'Failed') {
      this.toastConfig = {
        icon: 'warning',
        iconColor: '#CB2A2F',
        content: `Documento vinculado a ${linkedCount} de ${total} OS, pero el analisis IA no pudo iniciarse.`,
        msg: firstError || response.message,
        showUndo: false
      };
      return;
    }

    if (linkedCount < total) {
      this.toastConfig = {
        icon: 'warning',
        iconColor: '#CB2A2F',
        content: `Documento subido. Se vinculo a ${linkedCount} de ${total} OS.`,
        msg: firstError,
        showUndo: false
      };
      return;
    }

    if (batchResult.aiProcessingStatus === 'Completed') {
      this.toastConfig = {
        icon: 'check_circle',
        iconColor: '#6CDA75',
        content: `Documento subido y analisis IA completado para ${total} OS.`,
        showUndo: false
      };
      return;
    }

    this.toastConfig = {
      icon: 'check_circle',
      iconColor: '#6CDA75',
      content: `Documento subido y analisis IA iniciado para ${total} OS.`,
      showUndo: false
    };
  }

  private getRequestErrorMessage(error: any, fallback: string = 'Ha ocurrido un error inesperado.'): string {
    return error?.error?.message || error?.message || fallback;
  }

  private updateCurrentGeneralData(): void {
    if (!this._docModalType) {
      return;
    }

    this.currentGeneralData = {...this.generalDatas.find(d => d.type === this._docModalType)!};
    if (this._docModalType === 'upload' && this.aiUploadMode) {
      this.currentGeneralData.title = 'Subir y analizar con IA';
      this.currentGeneralData.actionIcon = 'auto_awesome';
      this.currentGeneralData.actionTitle = 'Subir y analizar';
    }
  }

  showNotification(closeModal: boolean = true): void {
    this.loading = false;
    this.showToast();
    if (closeModal) {
      this.close();
    }
  }

  private showToast(): void {
    this.notRef = this.notificationService
      .create("blank", '', this.notificationTmpl, {nzPlacement: "bottomRight"});
  }
  onConfirm(): void {
    this.loading = true;
    const userId = this.loggedUser!.id;
    const partnerId = this.loggedUser!.partnerId;

    switch (this.docModalType) {
      case 'upload': {
        if (this.onlyReturnFiles) {
          const files: File[] = this.uploadForms.map(v => v.file);
          this.proccessFilesEvent.emit(files);
          this.close();
        } else {
          this.startUpload();
        }
        break;
      }
      case 'correct': {
        this.startUpload();
        // this.document!.validationStatus = 'ValidationPending';
        // this.document!.rejectionDate = undefined;
        // this.document!.rejectionReason = undefined;
        // this.document!.correctionDate = new Date().toDateString();
        break;
      }
      case 'validate': {
        const params: DocumentValidationCreate = {
          documentId: this.document!.id,
          partnerId: partnerId,
          validationStatus: 'Validated'
        }
        this.documentService.createDocumentValidation(params)
          .pipe(finalize(() => this.showNotification()))
          .subscribe({
          next: (rsp) => {
            this.toastConfig.content = 'Documento validado.';
            this.document!.validationStatus = 'Validated';
            this.toastConfig.icon = 'check_circle';
            this.toastConfig.iconColor = '#6CDA75';
            this.toastConfig.content = 'Documento validado.';
            if (!this.document!.documentValidations) {
              this.document!.documentValidations = [];
            }
            this.document!.documentValidations.push(rsp.data);
          },
          error: (e) => {
            this.toastConfig.icon = 'dangerous';
            this.toastConfig.iconColor = '#CB2A2F';
            this.toastConfig.content = 'Ha ocurrido un error validando el documento.';
            this.toastConfig.msg = e.error.message;
            console.error(e);
          }
        });
        break;
      }
      case 'delete': {
        const idToDelete = this.documentsToDelete?.map(d => d.id);
        if (idToDelete) {
          this.documentService.deleteDocument(idToDelete).subscribe();
        }
        this.deleteEvent.emit(this.documentsToDelete);
        this.toastConfig.content = 'Los archivos se han eliminado.';
        this.notRef = this.notificationService
          .create("blank", '', this.notificationTmpl, {nzPlacement: "bottomRight"});
        this.close();
        break;
      }
      case 'return': {
        const params: DocumentValidationCreate = {
          documentId: this.document!.id,
          partnerId: partnerId,
          validationStatus: 'ValidationPending',
          reason: this.rejectReason,
        }
        this.documentService.createDocumentValidation(params)
          .pipe(finalize(() => this.showNotification()))
          .subscribe({
            next: (rsp) => {
              this.toastConfig.content = 'Documento regresado a pendiente.';
              this.document!.validationStatus = 'ValidationPending';
              this.toastConfig.icon = 'check_circle';
              this.toastConfig.iconColor = '#6CDA75';
              this.toastConfig.content = 'Documento vuelto a pendiente.';
              if (!this.document!.documentValidations) {
                this.document!.documentValidations = [];
              }
              this.document!.documentValidations.push(rsp.data)
            },
            error: (e) => {
              this.toastConfig.icon = 'dangerous';
              this.toastConfig.iconColor = '#CB2A2F';
              this.toastConfig.content = 'Ha ocurrido un error validando el documento.';
              this.toastConfig.msg = e.error.message;
              console.error(e);
            }
          });
        break;
      }
      case 'reject': {
        const params: DocumentValidationCreate = {
          documentId: this.document!.id,
          partnerId: partnerId,
          validationStatus: 'NonValidated',
          reason: this.rejectReason,
        }
        this.documentService.createDocumentValidation(params)
          .pipe(finalize(() => this.showNotification()))
          .subscribe({
            next: (rsp) => {
              this.toastConfig.content = 'Documento rechazado.';
              this.document!.validationStatus = 'NonValidated';
              this.toastConfig.icon = 'check_circle';
              this.toastConfig.iconColor = '#6CDA75';
              this.toastConfig.content = 'Documento rechazado.';
              if (!this.document!.documentValidations) {
                this.document!.documentValidations = [];
              }
              this.document!.documentValidations.push(rsp.data)
            },
            error: (e) => {
              this.toastConfig.icon = 'dangerous';
              this.toastConfig.iconColor = '#CB2A2F';
              this.toastConfig.content = 'Ha ocurrido un error rechazando el documento.';
              this.toastConfig.msg = e.error.message;
              console.error(e);
            }
          });

        break;
      }
    }
  }


  // Metodo para prevenir el error en el navegador de una peticion erronea del nz-upload
  customUpload = (item: NzUploadXHRArgs): Subscription => {
    // Simula un retardo (opcional)
    setTimeout(() => {
      // Llama al callback de éxito para que nz-upload marque el archivo como subido
      item.onSuccess!({}, item.file, null);
    }, 0);
    // Retorna una suscripción vacía, ya que no se está haciendo una petición real
    return new Subscription();
  };


}


